// AIRLINEOPS - CREW SYSTEM
// Generates 200+ crew with names, stats, certifications

const FIRST_NAMES_MALE = [
  "James", "Michael", "Robert", "David", "William", "Richard", "Joseph", "Thomas",
  "Christopher", "Daniel", "Matthew", "Anthony", "Mark", "Donald", "Steven", "Paul",
  "Andrew", "Joshua", "Kenneth", "Kevin", "Brian", "George", "Timothy", "Ronald",
  "Edward", "Jason", "Jeffrey", "Ryan", "Jacob", "Gary", "Nicholas", "Eric",
  "Jonathan", "Stephen", "Larry", "Justin", "Scott", "Brandon", "Benjamin", "Samuel"
];

const FIRST_NAMES_FEMALE = [
  "Mary", "Patricia", "Jennifer", "Linda", "Barbara", "Elizabeth", "Susan", "Jessica",
  "Sarah", "Karen", "Lisa", "Nancy", "Betty", "Margaret", "Sandra", "Ashley",
  "Kimberly", "Emily", "Donna", "Michelle", "Dorothy", "Carol", "Amanda", "Melissa",
  "Deborah", "Stephanie", "Rebecca", "Sharon", "Laura", "Cynthia", "Kathleen", "Amy",
  "Angela", "Shirley", "Anna", "Brenda", "Pamela", "Emma", "Nicole", "Helen"
];

const LAST_NAMES = [
  "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis",
  "Rodriguez", "Martinez", "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson", "Thomas",
  "Taylor", "Moore", "Jackson", "Martin", "Lee", "Perez", "Thompson", "White",
  "Harris", "Sanchez", "Clark", "Ramirez", "Lewis", "Robinson", "Walker", "Young",
  "Allen", "King", "Wright", "Scott", "Torres", "Nguyen", "Hill", "Flores",
  "Green", "Adams", "Nelson", "Baker", "Hall", "Rivera", "Campbell", "Mitchell",
  "Carter", "Roberts", "Gomez", "Phillips", "Evans", "Turner", "Diaz", "Parker"
];

export const PERSONALITY_TRAITS = {
  leader: { name: "Leader", description: "+10% team performance when captain" },
  mentor: { name: "Mentor", description: "Junior crew gain +25% experience" },
  hotHead: { name: "Hot Head", description: "-15% performance when paired with another Hot Head" },
  byTheBook: { name: "By-the-Book", description: "+5% safety, -5% speed" },
  cowboy: { name: "Cowboy", description: "-5% safety, +5% speed" },
  teamPlayer: { name: "Team Player", description: "+5% when paired with Leader" },
  loner: { name: "Loner", description: "-5% performance in any pairing" },
  coolUnderPressure: { name: "Cool Under Pressure", description: "+15% performance during delays/emergencies" },
  efficient: { name: "Efficient", description: "-2 minutes on turnaround phases" },
  experienced: { name: "Seasoned Veteran", description: "+10% passenger satisfaction" }
};

const TRAIT_KEYS = Object.keys(PERSONALITY_TRAITS);

export const AIRCRAFT_TYPE_RATINGS = [
  "E170", "E175", "E190", "E195",
  "CRJ700", "CRJ900",
  "A220", "A319", "A320", "A321",
  "B737", "B757",
  "B767", "B777", "B787",
  "A330", "A350", "A380"
];

function generateCrewId(role, index) {
  const prefix = role === "captain" ? "CPT" : role === "firstOfficer" ? "FO" : "FA";
  return `${prefix}-${String(index).padStart(4, '0')}`;
}

function generateName() {
  const isMale = Math.random() > 0.5;
  const firstName = isMale 
    ? FIRST_NAMES_MALE[Math.floor(Math.random() * FIRST_NAMES_MALE.length)]
    : FIRST_NAMES_FEMALE[Math.floor(Math.random() * FIRST_NAMES_FEMALE.length)];
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  return { firstName, lastName, fullName: `${firstName} ${lastName}`, gender: isMale ? "M" : "F" };
}

function generateStats(role, experienceYears) {
  const expFactor = Math.min(experienceYears / 20, 1);
  return {
    experience: Math.min(10, Math.floor(1 + experienceYears / 2)),
    reliability: Math.floor(70 + Math.random() * 25 + expFactor * 5),
    morale: Math.floor(60 + Math.random() * 30),
    fatigue: Math.floor(Math.random() * 20),
    skillRating: Math.floor(50 + Math.random() * 30 + expFactor * 20),
  };
}

function generateTypeRatings(role, experienceYears) {
  if (role === "flightAttendant") return [];
  
  const numRatings = Math.min(Math.floor(1 + experienceYears / 4), 6);
  const availableTypes = experienceYears < 5 
    ? AIRCRAFT_TYPE_RATINGS.slice(0, 6)
    : experienceYears < 10
    ? AIRCRAFT_TYPE_RATINGS.slice(0, 12)
    : AIRCRAFT_TYPE_RATINGS;
  
  const shuffled = [...availableTypes].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, numRatings);
}

function generateSpecialCerts(role, experienceYears) {
  const certs = [];
  const expFactor = experienceYears / 20;
  
  if (role !== "flightAttendant") {
    if (Math.random() < 0.3 + expFactor * 0.4) certs.push("etops");
    if (Math.random() < 0.2 + expFactor * 0.3) certs.push("catIII");
    if (experienceYears > 10 && Math.random() < 0.2) certs.push("instructor");
    if (experienceYears > 15 && Math.random() < 0.1) certs.push("checkAirman");
  }
  
  if (Math.random() < 0.3) certs.push("international");
  if (role === "flightAttendant" && Math.random() < 0.4) certs.push("hazmat");
  
  return certs;
}

function generateTrait() {
  if (Math.random() > 0.7) return null;
  return TRAIT_KEYS[Math.floor(Math.random() * TRAIT_KEYS.length)];
}

function generateSalary(role, experienceYears) {
  const baseSalaries = {
    captain: 180000,
    firstOfficer: 95000,
    flightAttendant: 45000
  };
  
  const base = baseSalaries[role];
  const expBonus = experienceYears * (role === "captain" ? 8000 : role === "firstOfficer" ? 4000 : 1500);
  
  return Math.floor(base + expBonus + (Math.random() * 10000 - 5000));
}

function generateCrewMember(role, index) {
  const name = generateName();
  const experienceYears = Math.floor(Math.random() * 25) + 1;
  const hireDate = new Date();
  hireDate.setFullYear(hireDate.getFullYear() - experienceYears);
  
  return {
    id: generateCrewId(role, index),
    ...name,
    role,
    experienceYears,
    hireDate: hireDate.toISOString().split('T')[0],
    ...generateStats(role, experienceYears),
    typeRatings: generateTypeRatings(role, experienceYears),
    specialCerts: generateSpecialCerts(role, experienceYears),
    trait: generateTrait(),
    salary: generateSalary(role, experienceYears),
    homeBase: null,
    dutyStatus: "available",
    currentDutyStart: null,
    flightTimeToday: 0,
    flightTimeLast28Days: 0,
    flightTimeLast365Days: 0,
    lastRestEnd: new Date().toISOString(),
    hoursUntilRequired30HrRest: 168,
    currentFlightId: null,
    nextAssignmentTime: null
  };
}

export function generateCrewPool(config = {}) {
  const {
    captains = 40,
    firstOfficers = 50,
    flightAttendants = 120,
    homeBase = "KORD"
  } = config;
  
  const crew = [];
  let index = 1;
  
  for (let i = 0; i < captains; i++) {
    const member = generateCrewMember("captain", index++);
    member.homeBase = homeBase;
    crew.push(member);
  }
  
  for (let i = 0; i < firstOfficers; i++) {
    const member = generateCrewMember("firstOfficer", index++);
    member.homeBase = homeBase;
    crew.push(member);
  }
  
  for (let i = 0; i < flightAttendants; i++) {
    const member = generateCrewMember("flightAttendant", index++);
    member.homeBase = homeBase;
    crew.push(member);
  }
  
  return crew;
}

export function checkCrewLegality(crewMember) {
  const issues = [];
  
  if (crewMember.dutyStatus === "resting") {
    const restEnd = new Date(crewMember.lastRestEnd);
    const now = new Date();
    const restHours = (now - restEnd) / (1000 * 60 * 60);
    if (restHours < 10) {
      issues.push({ type: "MIN_REST", message: `Needs ${(10 - restHours).toFixed(1)} more hours rest`, severity: "blocking" });
    }
  }
  
  if (crewMember.flightTimeLast28Days >= 100) {
    issues.push({ type: "28_DAY_LIMIT", message: "100 hour limit reached", severity: "blocking" });
  }
  
  if (crewMember.hoursUntilRequired30HrRest <= 0) {
    issues.push({ type: "30_HR_REST", message: "30hr rest required", severity: "blocking" });
  }
  
  if (crewMember.flightTimeToday >= 8) {
    issues.push({ type: "DAILY_LIMIT", message: "Daily limit reached", severity: crewMember.flightTimeToday >= 9 ? "blocking" : "warning" });
  }
  
  return { isLegal: !issues.some(i => i.severity === "blocking"), issues };
}

export function getAvailableCrew(crewPool, role, aircraftType = null) {
  return crewPool.filter(c => {
    if (c.role !== role) return false;
    if (c.dutyStatus !== "available") return false;
    
    if (role !== "flightAttendant" && aircraftType) {
      const getFamily = (t) => {
        if (t.startsWith("E1")) return "E1";
        if (t.startsWith("CRJ")) return "CRJ";
        if (t.startsWith("A22")) return "A220";
        if (t.startsWith("A31") || t.startsWith("A32")) return "A320";
        if (t.startsWith("A33")) return "A330";
        if (t.startsWith("A35")) return "A350";
        if (t.startsWith("B737") || t.startsWith("737")) return "B737";
        if (t.startsWith("B757") || t.startsWith("757")) return "B757";
        if (t.startsWith("B767") || t.startsWith("767")) return "B767";
        if (t.startsWith("B777") || t.startsWith("777")) return "B777";
        if (t.startsWith("B787") || t.startsWith("787")) return "B787";
        return t;
      };
      const targetFamily = getFamily(aircraftType);
      const hasRating = c.typeRatings.some(r => getFamily(r) === targetFamily);
      if (!hasRating) return false;
    }
    
    const legality = checkCrewLegality(c);
    if (!legality.isLegal) return false;
    
    return true;
  });
}

export default {
  generateCrewPool,
  generateCrewMember,
  checkCrewLegality,
  getAvailableCrew,
  PERSONALITY_TRAITS,
  AIRCRAFT_TYPE_RATINGS
};